export const announcementProgressLabels = {
  pending: 'To be processed', processed: 'Processed', excluded: 'No dividend needed',
};
export type AnnouncementProgressState = keyof typeof announcementProgressLabels;
export interface AnnouncementProgress {
  processingStatus: AnnouncementProgressState;
  processingExplanation: string;
  dividendIds: string[];
}
export interface AnnouncementProgressSource {
  id: string; status: string; exDate: string; reason: string; canonicalId?: string; dividendIds: string[];
}
/** Presentation only: recording an entitlement never implies that cash has been received. */
export function announcementProgress(row: AnnouncementProgressSource, sources: Map<string, AnnouncementProgressSource>, visited = new Set<string>()): AnnouncementProgress {
  const result = (processingStatus: AnnouncementProgressState, processingExplanation: string, dividendIds = row.dividendIds): AnnouncementProgress => ({ processingStatus, processingExplanation, dividendIds });
  if (visited.has(row.id)) return result('pending', 'The retained announcement link needs review.', []);
  visited.add(row.id);
  if (row.status === 'duplicate') {
    const original = row.canonicalId && sources.get(row.canonicalId);
    if (!original) return result('pending', 'The retained announcement is unavailable. Review before processing.', []);
    const progress = announcementProgress(original, sources, visited);
    return { ...progress, processingExplanation: `Duplicate announcement. ${progress.processingExplanation}` };
  }
  if (row.status === 'historical') return result('excluded', 'No eligible holding on the ex-dividend date.', []);
  if (row.status === 'rejected') return result('excluded', 'Rejected announcement. No dividend will be created.', []);
  if (['recorded', 'legacy-accepted', 'legacy-unknown', 'open'].includes(row.status)) {
    if (row.dividendIds.length) return result('processed', 'Dividend recorded. Track payment in Dividends.');
    if (row.status === 'recorded' || row.status === 'open') return result('pending', 'The linked dividend is unavailable. Review before processing.', []);
    return result('excluded', row.status === 'legacy-unknown'
      ? 'Imported announcement kept outside automatic processing. Payment status is unknown.'
      : 'Accepted imported history. No dividend is linked and no historical backfill will be created.', []);
  }
  if (row.reason === 'Awaiting confirmation of dividend amount.') return result('pending', 'Waiting for the dividend amount.');
  if (row.reason.startsWith('Future ex-date;')) return result('pending', `Waiting for the ex-dividend date (${row.exDate}).`);
  return result('pending', row.status === 'conflict' ? row.reason || 'Review the announcement before processing.'
    : row.dividendIds.length ? 'Some dividends are recorded; remaining holdings are to be processed.'
      : 'Will be processed by Prepare entitlements when the announcement is ready.');
}
